// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentDetail.test.tsx
//
// The open comment in the drawer (Figma November 2026, frame 0c "Comment
// drawer"): byline, title, risk and type chips, its Status, the changed
// window's before and after, then the Issue / Fix / Spec body and the
// approved spec with Show source.
import { Badge, Separator, Text } from "@radix-ui/themes";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { type AskV2ReviewCommentCardProps } from "./AskV2ReviewCommentCard";
import {
  FoldedIntoSubheading,
  JudgmentReceiptLink,
  reviewCommentPermissions,
  SpecQuoteLine,
} from "./AskV2ReviewCommentCardParts";
import {
  AskV2ReviewCommentChange,
  type CommentChange,
} from "./AskV2ReviewCommentChange";
import {
  AskV2ReviewCommentEditor,
  AskV2ReviewOwnCommentActions,
} from "./AskV2ReviewCommentEditor";
import { AskV2ReviewCommentStatus } from "./AskV2ReviewCommentStatus";
import { AskV2ReviewCompanionRow } from "./AskV2ReviewCompanionRow";
import {
  PreviouslyConfirmedSubheading,
  PreviouslyRejectedSubheading,
} from "./AskV2ReviewJudgmentReceipt";
import { AskV2ReviewRejectedHereSubheading } from "./AskV2ReviewRejectedHereSubheading";
import { CommentTagBadges } from "./CommentTagBadges";
import { severityPinTone, severityRank } from "./pinGroups";
import { type CommentWithOptionalTags } from "./reviewCommentTags";

import {
  AskPunttReviewCommentBadge,
  AskPunttReviewCommentBody,
  AskPunttReviewCommentSource,
  formatReviewCommentTime,
  reviewCommentAuthorLabel,
  reviewCommentTitle,
} from "../../components/ask-puntt-review";
import { reviewCommentGroup } from "../../utils/visibleReviewComments";

export type AskV2ReviewCommentDetailProps = Omit<
  AskV2ReviewCommentCardProps,
  "expanded" | "rejecting" | "onSelect" | "onClear" | "rowActions"
> & {
  /** Where the comment sits in a window that changed since the earlier version. */
  change?: CommentChange | null;
  onShowComparison?: (number: number) => void;
};

const RISK_COLOR = { high: "red", medium: "amber", low: "gray" } as const;
const RISK_BY_RANK = ["low", "medium", "high"] as const;

function RiskBadge({ comment }: { comment: CommentWithOptionalTags }) {
  const { t } = useTranslation("ui");
  // why: graded as `riskCounts` grades it, so the section's "1 high" opens
  // onto a High risk chip; a person's own note carries no risk at all.
  if (comment.severity == null && !comment.isRequired) return null;
  const severity = RISK_BY_RANK[severityRank(comment)];
  return (
    <Badge
      size="1"
      variant="soft"
      color={RISK_COLOR[severity]}
      data-testid={`ask-v2-review-detail-risk-${comment._id}`}
      data-risk={severity}
    >
      {t(`askV2.review.drawer.risk.${severity}`)}
    </Badge>
  );
}

export function AskV2ReviewCommentDetail({
  comment,
  commentNumber,
  companions,
  rejectingId = null,
  authorOptions,
  claimCitations,
  memoryCitations,
  contextFiles,
  reviewFiles,
  currentUserId,
  authorNameByUserId,
  currentUserName,
  judgmentPending,
  onJudge,
  onEdit,
  onDelete,
  onRejectingChange,
  change,
  onShowComparison,
}: AskV2ReviewCommentDetailProps) {
  const { t } = useTranslation("ui");
  const id = comment._id;
  const [editing, setEditing] = useState<{
    draft: string;
    failed: boolean;
  } | null>(null);
  const { canJudge, canEdit, canDelete } = reviewCommentPermissions({
    comment,
    currentUserId,
    onJudge,
    onEdit,
    onDelete,
  });
  const saveEdit = (description: string) => {
    setEditing(null);
    onEdit?.(id, description).catch(() => {
      setEditing({ draft: description, failed: true });
    });
  };

  return (
    <li
      className="relative flex flex-col gap-3.5 pt-3"
      data-testid={`ask-v2-review-detail-${id}`}
    >
      <div className="flex flex-col gap-2">
        <div
          className="flex min-w-0 items-center gap-2 pr-16 text-[13px]"
          data-testid={`ask-v2-review-detail-byline-${id}`}
        >
          <AskPunttReviewCommentBadge
            number={commentNumber}
            tone={severityPinTone(comment)}
            className="!size-6"
            data-testid={`ask-v2-review-comment-index-${id}`}
          />
          <span className="truncate font-medium text-puntt-neutral-gray-12">
            {reviewCommentAuthorLabel(comment, authorOptions)}
          </span>
          <span className="shrink-0 text-puntt-neutral-gray-11">
            · {formatReviewCommentTime(comment.createdAt)}
          </span>
          {!comment.isAI && comment.editedAt != null ? (
            <span
              className="shrink-0 text-puntt-neutral-gray-11"
              data-testid={`ask-v2-review-comment-edited-${id}`}
            >
              {t("askV2.review.commentEdited")}
            </span>
          ) : null}
        </div>
        <Text
          as="p"
          weight="medium"
          className="text-[17px] leading-[23px] text-puntt-neutral-gray-12"
          data-testid={`ask-v2-review-comment-title-${id}`}
        >
          {reviewCommentTitle(comment)}
        </Text>
        <AskV2ReviewRejectedHereSubheading
          comment={comment}
          commentId={id}
          authorNameByUserId={authorNameByUserId}
          currentUserId={currentUserId}
          currentUserName={currentUserName}
        />
        <PreviouslyRejectedSubheading
          comment={comment}
          commentId={id}
          currentUserId={currentUserId}
        />
        <PreviouslyConfirmedSubheading
          comment={comment}
          commentId={id}
          currentUserId={currentUserId}
        />
        <FoldedIntoSubheading comment={comment} commentId={id} />
        <div className="flex flex-wrap gap-1.5">
          {comment.isRequired ? (
            <Badge
              size="1"
              variant="solid"
              color="blue"
              data-testid={`ask-v2-review-comment-urgent-badge-${id}`}
            >
              {t("askV2.review.urgentBadge")}
            </Badge>
          ) : null}
          <RiskBadge comment={comment} />
          <CommentTagBadges comment={comment} commentId={id} />
        </div>
      </div>
      {canJudge ? (
        <AskV2ReviewCommentStatus
          commentId={id}
          liked={comment.liked}
          rejected={reviewCommentGroup(comment) === "rejected"}
          disabled={judgmentPending}
          onJudge={(liked, rejection) => onJudge?.(id, liked, rejection)}
        />
      ) : null}
      <Separator size="4" />
      {change ? (
        <AskV2ReviewCommentChange
          commentId={id}
          change={change}
          onShowComparison={onShowComparison}
        />
      ) : null}
      <div data-testid={`ask-v2-review-comment-expanded-${id}`}>
        <JudgmentReceiptLink comment={comment} commentId={id} />
        {canEdit && editing != null ? (
          <AskV2ReviewCommentEditor
            commentId={id}
            saved={comment.description}
            initialDraft={editing.draft}
            failed={editing.failed}
            onSave={saveEdit}
            onCancel={() => setEditing(null)}
          />
        ) : (
          <AskPunttReviewCommentBody
            commentId={id}
            description={comment.description}
            claimCitations={claimCitations}
            memoryCitations={memoryCitations}
            afterExpand={
              <>
                <SpecQuoteLine comment={comment} />
                <AskPunttReviewCommentSource
                  comment={comment}
                  contextFiles={contextFiles}
                  reviewFiles={reviewFiles}
                />
              </>
            }
          />
        )}
      </div>
      {companions?.map((companion) => (
        <AskV2ReviewCompanionRow
          key={companion._id}
          comment={companion}
          rejecting={rejectingId === companion._id}
          judgmentPending={judgmentPending}
          onRejectingChange={onRejectingChange}
          onJudge={onJudge}
        />
      ))}
      {editing == null ? (
        <AskV2ReviewOwnCommentActions
          commentId={id}
          pinned
          onDelete={canDelete ? onDelete : undefined}
          onRequestEdit={
            canEdit
              ? () => setEditing({ draft: comment.description, failed: false })
              : undefined
          }
        />
      ) : null}
    </li>
  );
}
